import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { RootCauseMappingEnvService } from './root-cause-mapping.env.service';
import {
  TRCAMappingCrudRequest,
  TRCAMappingCrudResponse,
  TRCAHierarchicalResponse,
  TRCACategory,
  TRCAMapping,
} from '@monorepo/types';

@Injectable({
  providedIn: 'root',
})
export class RootCauseMappingDataService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-type': 'application/json',
    }),
  };

  constructor(private http: HttpClient, private rootCauseMappingEnvService: RootCauseMappingEnvService) {}

  // --- Upload RCA File ---
  uploadRCAFileURL(): string {
    const URL = this.rootCauseMappingEnvService.getEnvVar('RCA_UPLOAD');
    return URL;
  }

  // --- Download Template ---
  downloadTemplateURL() {
    return this.rootCauseMappingEnvService.getEnvVar('RCA_TEMPLATE');
  }

  downloadErrorFileURL() {
    return this.rootCauseMappingEnvService.getEnvVar('RCA_DOWNLOAD_ERROR_FILE');
  }

  // --- Create/Update/Delete Mapping ---
  crudMapping(payload: TRCAMappingCrudRequest): Observable<TRCAMappingCrudResponse> {
    const URL = this.rootCauseMappingEnvService.getEnvVar('RCA_MAPPINGS_CRUD');
    return this.http.post<TRCAMappingCrudResponse>(URL, payload, this.httpOptions);
  }

  // --- Get Hierarchical Data ---
  getHierarchicalData(factoryId: string): Observable<TRCAHierarchicalResponse> {
    const URL = this.rootCauseMappingEnvService.getHierarchicalUrl(factoryId);
    return this.http.get<TRCAHierarchicalResponse>(URL, this.httpOptions);
  }

  // --- Convenience methods for CRUD operations ---
  createMapping(
    factoryId: string,
    rootCauseName: string,
    rootCauseCategory: TRCACategory,
    correctiveActions: Array<{ name: string }>,
    preventiveActions: Array<{ name: string }>,
  ): Observable<TRCAMappingCrudResponse> {
    const payload: TRCAMappingCrudRequest = {
      operation: 'CREATE',
      rootCauseName,
      rootCauseCategory,
      factoryId,
      correctiveActions: correctiveActions.map((action) => ({ name: action.name })),
      preventiveActions: preventiveActions.map((action) => ({ name: action.name })),
    };
    return this.crudMapping(payload);
  }

  updateMapping(
    factoryId: string,
    rootCauseId: number,
    rootCauseName: string,
    rootCauseCategory: TRCACategory,
    correctiveActions: Array<{ id?: number | null; name: string }>,
    preventiveActions: Array<{ id?: number | null; name: string }>,
  ): Observable<TRCAMappingCrudResponse> {
    const payload: TRCAMappingCrudRequest = {
      operation: 'UPDATE',
      rootCauseId,
      rootCauseName,
      rootCauseCategory,
      factoryId,
      correctiveActions,
      preventiveActions,
    };
    return this.crudMapping(payload);
  }

  deleteMapping(
    factoryId: string,
    rootCause: TRCAMapping,
    category: TRCACategory,
  ): Observable<TRCAMappingCrudResponse> {
    const payload: TRCAMappingCrudRequest = {
      operation: 'DELETE',
      rootCauseId: rootCause.id,
      rootCauseName: rootCause.name,
      rootCauseCategory: category,
      factoryId,
      correctiveActions: rootCause.correctiveActions,
      preventiveActions: rootCause.preventiveActions || [],
    };
    return this.crudMapping(payload);
  }
}
